import React, { useState, useEffect, useRef } from 'react';

const ScrollProgressBar = ({ filteredCount }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const progressBarRef = useRef(null);

  // Update scroll progress when content changes
  useEffect(() => {
    const handleScroll = () => {
      if (isDragging) return; // Don't update while dragging
      
      const scrollContainer = document.querySelector('.app-content');
      if (!scrollContainer) return;
      
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
      
      // Prevent division by zero when content fits in viewport
      if (scrollHeight <= 0) {
        setScrollProgress(0);
        return;
      }
      
      const progress = (scrollTop / scrollHeight) * 100;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };

    const scrollContainer = document.querySelector('.app-content');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      // Initial calculation to show cursor from the start
      handleScroll();
    } else {
      // If container not ready, try again shortly
      const timer = setTimeout(() => {
        const container = document.querySelector('.app-content');
        if (container) {
          container.addEventListener('scroll', handleScroll);
          handleScroll();
        }
      }, 100);
      return () => clearTimeout(timer);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isDragging, filteredCount]); // Recalculate when filters change

  const scrollToProgress = (progressPercent) => {
    const scrollContainer = document.querySelector('.app-content');
    if (!scrollContainer) return;
    
    const scrollHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
    const targetScroll = (progressPercent / 100) * scrollHeight;
    
    scrollContainer.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    });
  };

  const handleBarClick = (e) => {
    if (e.target.classList.contains('scroll-progress-bar__cursor')) return;
    if (!progressBarRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressPercent = (clickX / rect.width) * 100;
    
    setScrollProgress(progressPercent);
    scrollToProgress(progressPercent);
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    handleDrag(e);
  };

  const handleDrag = (e) => {
    if (!progressBarRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const dragX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const progressPercent = (dragX / rect.width) * 100;
    
    setScrollProgress(progressPercent);
  };

  const handleDragEnd = (e) => {
    if (isDragging) {
      handleDrag(e);
      scrollToProgress(scrollProgress);
      setIsDragging(false);
    }
  };

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDrag(e);
      const handleMouseUp = (e) => handleDragEnd(e);
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, scrollProgress]);

  return (
    <div 
      ref={progressBarRef}
      className="scroll-progress-bar"
      onClick={handleBarClick}
    >
      <div 
        className="scroll-progress-bar__track" 
      />
      <div 
        className="scroll-progress-bar__fill" 
        style={{ width: `${scrollProgress}%` }}
      />
      
      {/* Draggable thumb */}
      <div
        className="scroll-progress-bar__cursor"
        style={{ left: `${scrollProgress}%` }}
        onMouseDown={handleDragStart}
      />
    </div>
  );
};

export default ScrollProgressBar;
